import React, { useState, useCallback, useEffect, useRef } from 'react';
import { RMSIconButton } from '../../components/buttons/index';
import AddIcon from '@material-ui/icons/Add';
import PersonIcon from '@material-ui/icons/PersonOutlined';
import { notify } from 'reducers/NotifierReducer';
import { getFullPermissions } from 'reducers/PermissionsReducer';
import { useDispatch } from 'react-redux';
import { RMSAutocomplete } from '../../../shared/components/form-controls/index';
import generalServices, { getPlaceById } from '../../services/generalServices';
import { executeApiAction } from '../../utility/apiHelper';
import { isEmpty } from 'features/shared/utility/helper';

const SearchPlaceAutoComplete = ({
  name,
  label,
  onSelectionChange,
  placeholder,
  defaultPlaceId = null,
  defaultPlaceSelectedCallback = null,
  showAddIcon = true,
  onAddIconClick = () => {},
  showDetailsIcon = true,
  disabled = false,
}) => {
  const dispatch = useDispatch();
  const isMounted = useRef(true);
  // Matches legacy's RMSPlaceLookup/index.js exactly — the inline Add-place icon is hidden
  // (not disabled) unless the user has Places.Create.
  const placePerms = getFullPermissions('globals', 'Places', 'any');
  const canCreatePlace = placePerms.Create;
  // Beyond legacy (which always shows this icon unconditionally) — the Place Details icon
  // is now hidden entirely unless the user has at least Read or Edit on Places.
  const canViewPlaceDetails = Boolean(placePerms.Read || placePerms.Edit);
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(false);

  const searchPlaces = async (searchText) => {
    await executeApiAction({
      setLoading,
      action: async () => {
        return generalServices.getPlacesBySearch(searchText);
      },
      onSuccess: (response) => setPlaces(response),
      onError: (error) => {
        dispatch(notify(error.message, 'error'));
      },
    });
  };

  const handleDefaultPlaceId = useCallback(
    async (placeId) => {
      if (placeId) {
        await executeApiAction({
          setLoading,
          dispatch: dispatch,
          action: async () => {
            return getPlaceById(placeId);
          },
          onSuccess: (response) => {
            setPlaces([response]);
            defaultPlaceSelectedCallback?.(response);
          },
        });
      }
    },
    [defaultPlaceSelectedCallback, dispatch]
  );

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isEmpty(defaultPlaceId)) return;
    handleDefaultPlaceId(defaultPlaceId);
  }, [defaultPlaceId, handleDefaultPlaceId]);

  return (
    <>
      <RMSAutocomplete
        name={name}
        label={label ?? 'Place'}
        placeholder={placeholder}
        getOptionLabel={(option) => `${option.PlaceName}`}
        optionLabel="PlaceName"
        optionValue="ptsPlaceID"
        options={places}
        leftContent={() =>
          showAddIcon &&
          canCreatePlace && (
            <RMSIconButton
              icon={<AddIcon fontSize="small" />}
              tooltip="Add Place"
              onClick={(event) => {
                event.stopPropagation();
                onAddIconClick?.(event);
              }}
              size="small"
            />
          )
        }
        rightContent={({ value }) =>
          showDetailsIcon &&
          value &&
          canViewPlaceDetails && (
            <RMSIconButton
              icon={<PersonIcon />}
              tooltip="Place Details"
              size="small"
              onClick={() => dispatch(notify('Coming Soon', 'info'))}
            />
          )
        }
        onSelectionChange={(data) => onSelectionChange && onSelectionChange(data)}
        onSearch={searchPlaces}
        disabled={disabled}
        loading={loading}
      />
    </>
  );
};

export default SearchPlaceAutoComplete;
